import React, {FC, useState, useEffect} from "react"

import {OrganisationalSummaryCategory} from "@irati/types"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"
import authentication from "@irati/auth"

import RecognitionModal from "../../Dashboard/Reporting/Widgets/Recognition/RecognitionModal"
import WellnessModal from "../../Dashboard/Reporting/Widgets/Wellness/WellnessModal"
import {CostOfLivingWidget} from "../../Dashboard/Reporting/Widgets"

export const TREND_RATE_HUBS_DISMISS = "trendRate_dismiss"
export const TREND_RATE_HUB_VISIBLE = "trendRate_visible"

interface TrendHub {
  name: string
  active: boolean
  title: string
  link: string
  onClick: () => void
}

interface Props {
  selectedCategory: OrganisationalSummaryCategory
}

const QuickActions: FC<Props> = ({selectedCategory}) => {
  const [categoryDetails, setCategoryDetails] = useState<TrendHub | null>()
  const [flexiPerksModal, setFlexiPerksModal] = useState(false)
  const [recognitionsModal, setRecognitionsModal] = useState(false)
  const [welnessModal, setWellnessModal] = useState(false)

  const TRENDS_HUBS: TrendHub[] = [
    {
      name: OrganisationalSummaryCategory.Perks,
      active: !!authentication.apps("perks"),
      title: "admin.reporting.orgSummary.quickActions.alert.title1",
      link: "admin.reporting.orgSummary.quickActions.alert.cta1",
      onClick: () => setFlexiPerksModal(true)
    },
    {
      name: OrganisationalSummaryCategory.Celebrations,
      active: !!authentication.apps("recognition"),
      title: "admin.reporting.orgSummary.quickActions.alert.title2",
      link: "admin.reporting.orgSummary.quickActions.alert.cta2",
      onClick: () => setRecognitionsModal(true)
    },
    {
      name: OrganisationalSummaryCategory.Wellness,
      active: !!authentication.apps("wellnessHub"),
      title: "admin.reporting.orgSummary.quickActions.alert.title3",
      link: "admin.reporting.orgSummary.quickActions.alert.cta3",
      onClick: () => setWellnessModal(true)
    }
  ]

  const isHubDismissed = (value: string) => {
    return localStorage.getItem(TREND_RATE_HUBS_DISMISS)?.includes(value)
  }

  const handleTrendHub = (index: number) => {
    if (TRENDS_HUBS[index].active && !isHubDismissed(TRENDS_HUBS[index].name)) {
      setCategoryDetails(TRENDS_HUBS[index])
    } else {
      setCategoryDetails(null)
    }
  }

  const handleCategorySwitch = (category: OrganisationalSummaryCategory) => {
    switch (category) {
      case OrganisationalSummaryCategory.Perks:
        return handleTrendHub(0)
      case OrganisationalSummaryCategory.Celebrations:
        return handleTrendHub(1)
      case OrganisationalSummaryCategory.Wellness:
        return handleTrendHub(2)
      case OrganisationalSummaryCategory.Main:
        return handleMain()
    }
  }

  const setActiveHub = (activeHub: TrendHub) => {
    setCategoryDetails(activeHub)
    localStorage.setItem(TREND_RATE_HUB_VISIBLE, `${activeHub.name} {}`)
  }

  const handleNextIndex = (currentIndex: number) => {
    const activeHubs = TRENDS_HUBS.filter((h) => h.active)
    if (currentIndex + 1 < activeHubs.length) {
      setActiveHub(TRENDS_HUBS[currentIndex + 1])
    } else if (activeHubs[0]) {
      setActiveHub(activeHubs[0])
    }
  }

  const showFirstActiveHub = () => {
    const index = TRENDS_HUBS.findIndex((h) => h.active && !isHubDismissed(h.name))
    if (index > -1) {
      setActiveHub(TRENDS_HUBS[index])
    }
  }

  const showNextActiveHub = (category: string) => {
    const currentIndex = TRENDS_HUBS.findIndex((h) => h.name === category.split(" ")[0] && !isHubDismissed(h.name))
    if (category.includes("{}")) {
      setCategoryDetails(TRENDS_HUBS[currentIndex])
    } else {
      handleNextIndex(currentIndex)
    }
  }

  const handleMain = () => {
    setCategoryDetails(null)
    const storedCategory = localStorage.getItem(TREND_RATE_HUB_VISIBLE)
    if (!storedCategory) {
      showFirstActiveHub()
    } else {
      showNextActiveHub(storedCategory)
    }
  }

  const handleDismissAlert = (value: string) => {
    const dismissedHubs = localStorage.getItem(TREND_RATE_HUBS_DISMISS)
    localStorage.setItem(TREND_RATE_HUBS_DISMISS, dismissedHubs ? dismissedHubs?.concat(" ", value) : value)
    setCategoryDetails(null)
  }

  useEffect(() => {
    handleCategorySwitch(selectedCategory)
  }, [selectedCategory])

  return (
    <>
      {categoryDetails && (
        <Alert
          type={AlertTypes.tip}
          title={categoryDetails?.title}
          link={categoryDetails?.link}
          onClickLink={categoryDetails?.onClick}
          onHide={() => handleDismissAlert(categoryDetails.name)}
        />
      )}
      <RecognitionModal show={recognitionsModal} onHide={() => setRecognitionsModal(false)} />
      <WellnessModal show={welnessModal} showPromoteOnly onHide={() => setWellnessModal(false)} />
      <CostOfLivingWidget showNudgeModalOnly={flexiPerksModal} hideWidget onHide={() => setFlexiPerksModal(false)} />
    </>
  )
}

export default QuickActions
